import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Video from '../videos/Timeline 1.mp4';
import AnimatedText from './AnimatedText';

// Styled components
const LandingContainer = styled.div`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  isolation: isolate; /* NEW: keeps z-index layering contained */
`;


const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  background-color: #020617; /* dark fallback while loading */
`;
const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.10);
  z-index: 1;
  pointer-events: none; /* important: don't block clicks */
`;
const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 1.5rem; /* add side padding for small screens */
  max-width: 100%;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.03); /* slightly less aggressive scale */
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
    &:hover {
      transform: none; /* avoid zoom-in on mobile to prevent overflow */
    }
  }
`;
// Responsive style object for AnimatedText
const animatedTextStyle = {
  cursor: 'pointer',
  fontSize: 'clamp(2.6rem, 8vw, 5.2rem)',  // was smaller
  fontWeight: 'normal',
  letterSpacing: '0.12em',
  color: '#0a192f',
  transition: 'all 0.3s ease',
  lineHeight: 1.1,
  whiteSpace: 'normal',
};
// Elegant "button" wrapper for the CTA text
const HeroButton = styled.div`
  display: inline-block;
  cursor: pointer;
  border-radius: 2.5rem;
  border: 2px solid transparent;
  padding: 0.25em 0.9em;  /* tighter than 0.4em 1.5em */
  transition: border 0.3s, box-shadow 0.3s, background 0.3s;
  will-change: border, box-shadow, background;
  max-width: 100%;
  box-sizing: border-box;

  &:hover,
  &:focus {
    border: 2px solid #ffd70033;
    box-shadow: 0 0 16px #ffd70055;
    background: rgba(6, 49, 99, 0.28);
  }

  &:active {
    border: 2px solid #ffd70099;
    background: rgba(22, 21, 11, 0.09);
  }

  @media (max-width: 768px) {
    padding: 0.2em 0.7em;  /* even tighter on mobile */
  }
`;

const AnimatedButtonWrapper = styled.div`
  display: inline-block;
  position: relative;
  transition: filter 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: 100%;

  &:hover {
    filter: brightness(1.12);
  }

  &::after {
    content: '';
    display: block;
    margin: 0 auto;
    width: 42%;
    padding-top: 0.45em;
    border-bottom: 2px solid #ffd700a6;
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s, width 0.2s;
  }

  &:hover::after {
    opacity: 1;
    width: 60%;
  }
`;



const LandingPage = ({ onComplete }) => {
  const [animate, setAnimate] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const journeyStarted = localStorage.getItem('journeyStarted');

    if (journeyStarted) {
      const startedTime = parseInt(journeyStarted, 10);

      if (isNaN(startedTime)) {
        console.warn('Invalid journeyStarted timestamp detected, clearing it.');
        localStorage.removeItem('journeyStarted');
        return;
      }

      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;

      if (now - startedTime < sevenDays) {
        navigate('/home', { replace: true });
      }
    }
  }, [navigate]);

  const handleReveal = () => {
    setAnimate(true);

    setTimeout(() => {
      const nowString = Date.now().toString();
      localStorage.setItem('journeyStarted', nowString);
      onComplete();
      navigate('/home');
    }, 2000);
  };

  return (
    <LandingContainer>
      <BackgroundVideo
  autoPlay
  loop
  muted
  playsInline
  preload="auto"
  poster="/images/10246984.jpeg"  // create a lightweight jpg
>
  <source src={Video} type="video/mp4" />
  Your browser does not support the video tag.
</BackgroundVideo>
 <VideoOverlay />
      <ContentWrapper>
        <motion.div
          initial={{ opacity: 1, scale: 1 }}
          animate={animate ? { opacity: 0, scale: 2 } : {}}
          transition={{ duration: 2 }}
        >
          <HeroButton onClick={handleReveal} tabIndex={0}>
            <AnimatedButtonWrapper>
              <AnimatedText
                text="Start Your Journey"
                duration={1.8}
                delay={1.25}
                endColor="#0a192f"
                particleColor1="#0a192f"
                particleColor2="#ffd700"
                style={animatedTextStyle}
              />
            </AnimatedButtonWrapper>
          </HeroButton>
        </motion.div>
      </ContentWrapper>
    </LandingContainer>
  );
};

export default LandingPage;
